import { produce } from 'immer';
import { set } from 'lodash';
import updateConsolidatedCode from './dynamicCodeSnippetsHelper';
import { hardcodedContactData } from '../../../constants/constants';

const defaultState = {
  ButtonConfigurator: {
    labelOptions: [],
    colorOptions: [],
    shapeOptions: [],
    layoutOptions: [],
    buttonSettings: {
      color: '',
      label: '',
      layout: '',
      shape: ''
    }
  },
  SdkConfigurator: {
    sdkSettings: {
      clientId: '',
      buyerCountry: '',
      currency: '',
      sdkBaseUrl: '',
      type: '',
      enableFunding: '',
      disableFunding: ''
    },
    buyerCountryOptions: [],
    buyerCurrencyOptions: []
  },
  code: {
    frontend: [],
    backend: [],
    language: {
      frontend: '',
      backend: ''
    },
    status: { captureOrder: true },
    downloadCodeUrl: ''
  },
  pageContent: [],
  isLoading: false,
  OrderConfigurator: {
    amount: { value: '' },
    intent: '',
    sellerId: '',
    contactPreference: ''
  },
  ThreeDsConfigurator: {},
  lastCapture: { value: null },
  cardFieldsConfigurator: {
    value: {}
  },
  product: '',
  ShippingCallbackConfigurator: {
    shippingCallbackType: 'client'
  },
  fastlaneContext: {
    paymentWatermark: {},
    paymentComponent: {},
    cardComponent: {},
    identity: {},
    profile: {},
    isLoading: false
  },
  VaultSetupTokenConfigurator: {},
  appSwitch: false
};

export default function integrationPageSectionReducer(state = defaultState, action) {
  switch (action.type) {
    case 'UPDATE_BUTTON_SETTINGS':
      return produce(state, (draftState) => {
        draftState.ButtonConfigurator.buttonSettings[action.category] = action.value;

        const codeLanguage = {
          ...state.code.language,
          status: state.code.status
        };
        const newConsolidatedCode = updateConsolidatedCode(
          state.code.frontend,
          state.code.backend,
          state.SdkConfigurator.sdkSettings,
          codeLanguage,
          state.OrderConfigurator,
          draftState.ButtonConfigurator,
          draftState.cardFieldsConfigurator,
          state.product,
          state.integrationType,
          state.VaultSetupTokenConfigurator
        );
        draftState.code.consolidatedCode = newConsolidatedCode;
      });
    case 'UPDATE_SDK_SETTINGS':
      return produce(state, (draftState) => {
        draftState.SdkConfigurator.sdkSettings[action.category] = action.value;
        const codeLanguage = {
          ...state.code.language,
          status: state.code.status
        };
        const newConsolidatedCode = updateConsolidatedCode(
          state.code.frontend,
          state.code.backend,
          draftState.SdkConfigurator.sdkSettings,
          codeLanguage,
          state.OrderConfigurator,
          state.ButtonConfigurator,
          draftState.cardFieldsConfigurator,
          state.product,
          state.integrationType,
          state.VaultSetupTokenConfigurator
        );
        draftState.code.consolidatedCode = newConsolidatedCode;
        if (action.category === 'clientId') {
          draftState.lastCapture.value = null;
        }
      });
    case 'BATCH_UPDATE_SDK_SETTINGS':
      return produce(state, (draftState) => {
        const { value } = action;
        draftState.SdkConfigurator.sdkSettings = { ...state.SdkConfigurator.sdkSettings, ...value };
        const codeLanguage = {
          ...state.code.language,
          status: state.code.status
        };
        const newConsolidatedCode = updateConsolidatedCode(
          state.code.frontend,
          state.code.backend,
          draftState.SdkConfigurator.sdkSettings,
          codeLanguage,
          state.OrderConfigurator,
          state.ButtonConfigurator,
          draftState.cardFieldsConfigurator,
          state.product,
          state.integrationType,
          state.VaultSetupTokenConfigurator
        );
        draftState.code.consolidatedCode = newConsolidatedCode;
      });
    case 'UPDATE_DOWNLOAD_URL': {
      return produce(state, (draftState) => {
        draftState.code.downloadCodeUrl = action.value;
      });
    }
    case 'UPDATE_CODE_SELECTION':
      return produce(state, (draftState) => {
        draftState.code.language[action.category] = action.value;
        draftState.pageContent = action.contents;
        draftState.code.downloadCodeUrl = action.downloadCodeUrl;
        draftState.code = {
          ...draftState.code,
          ...action.codes
        };
        const codeLanguage = {
          ...draftState.code.language,
          status: state.code.status
        };
        const newConsolidatedCode = updateConsolidatedCode(
          draftState.code.frontend,
          draftState.code.backend,
          state.SdkConfigurator.sdkSettings,
          codeLanguage,
          state.OrderConfigurator,
          state.ButtonConfigurator,
          draftState.cardFieldsConfigurator,
          state.product,
          state.integrationType,
          state.VaultSetupTokenConfigurator
        );
        draftState.code.consolidatedCode = newConsolidatedCode;
      });
    case 'UPDATE_HIGHLIGHTED_CODEBLOCK':
      return produce(state, (draftState) => {
        const { codeBlock } = action;
        draftState.codeHighlight = { ...draftState.codeHighlight, codeBlock };
      });
    case 'UPDATE_CURRENT_SECTION_KEY':
      return produce(state, (draftState) => {
        const { currentSectionKey } = action;
        draftState.codeHighlight = { ...draftState.codeHighlight, currentSectionKey };
      });
    case 'TOGGLE_CODE_BLOCK':
      return produce(state, (draftState) => {
        const codeBlocks = [].concat(action.key);
        /*
          Its required to show code for both the above features, however whenever either feature is toggled
          off, its status is disabled irrespective of the status of the other feature.

          Given the parent content key ... access the status of all the corresponding child code blocks ...
          If all true, delete the parent content key ...
      */
        if (!action.value && codeBlocks.length > 1) {
          const parentContentKey = codeBlocks?.[0];

          // Get all matching child content keys from all metadata arrays other than currently toggled child
          const childContentKeys = state.code.consolidatedCode
            .flatMap((codeBlock) =>
              codeBlock.metadata.filter((entry) => entry.parentCodeBlock === parentContentKey)
            )
            .filter((child) => !codeBlocks.includes(child.key));

          // Remove parent element if every child's status is active
          if (
            childContentKeys.length > 0 &&
            childContentKeys.some((child) => state.code.status[child.key])
          ) {
            delete codeBlocks[0];
          }
        }
        codeBlocks.forEach((block) => {
          if (block.startsWith('-')) {
            draftState.code.status[block.slice(1)] = !action.value;
          } else {
            draftState.code.status[block] = action.value;
          }
        });
        const codeLanguage = {
          ...state.code.language,
          status: draftState.code.status
        };
        draftState.code.consolidatedCode = updateConsolidatedCode(
          draftState.code.frontend,
          draftState.code.backend,
          state.SdkConfigurator.sdkSettings,
          codeLanguage,
          state.OrderConfigurator,
          state.ButtonConfigurator,
          draftState.cardFieldsConfigurator,
          state.product,
          state.integrationType,
          state.VaultSetupTokenConfigurator
        );
      });
    case 'UPDATE_APP_SWITCH':
      return produce(state, (draftState) => {
        draftState.appSwitch = action.value;
      });
    case 'UPDATE_SELLER_ID':
      return produce(state, (draftState) => {
        draftState.OrderConfigurator.sellerId = action.value;
      });
    case 'UPDATE_BUYER_EMAIL_ADDRESS':
      return produce(state, (draftState) => {
        draftState.OrderConfigurator.buyerEmailAddress = action.value;
      });
    case 'UPDATE_AMOUNT':
      return produce(state, (draftState) => {
        draftState.OrderConfigurator.amount[action.category] = action.value;
        const codeLanguage = {
          ...state.code.language,
          status: state.code.status
        };
        draftState.code.consolidatedCode = updateConsolidatedCode(
          state.code.frontend,
          state.code.backend,
          state.SdkConfigurator.sdkSettings,
          codeLanguage,
          draftState.OrderConfigurator,
          state.ButtonConfigurator,
          draftState.cardFieldsConfigurator,
          state.product,
          state.integrationType,
          state.VaultSetupTokenConfigurator
        );
      });
    case 'UPDATE_VAULT_SETUP_OPTION':
      return produce(state, (draftState) => {
        const codeLanguage = {
          ...state.code.language,
          status: state.code.status
        };
        set(draftState.VaultSetupTokenConfigurator, action.key, action.value);
        draftState.code.consolidatedCode = updateConsolidatedCode(
          state.code.frontend,
          state.code.backend,
          state.SdkConfigurator.sdkSettings,
          codeLanguage,
          draftState.OrderConfigurator,
          state.ButtonConfigurator,
          state.cardFieldsConfigurator,
          state.product,
          state.integrationType,
          draftState.VaultSetupTokenConfigurator
        );
      });
    case 'ADD_PAYMENT_TOKEN':
      return produce(state, (draftState) => {
        if (!state.VaultSetupTokenConfigurator.paymentTokens.options.includes(action.value)) {
          draftState.VaultSetupTokenConfigurator.paymentTokens.options = [
            ...state.VaultSetupTokenConfigurator.paymentTokens.options,
            action.value
          ];
          if (state.VaultSetupTokenConfigurator.paymentTokens.options.length === 0) {
            draftState.VaultSetupTokenConfigurator.paymentTokens.selected = action.value;
          }
          const codeLanguage = {
            ...state.code.language,
            status: state.code.status
          };
          draftState.code.consolidatedCode = updateConsolidatedCode(
            state.code.frontend,
            state.code.backend,
            state.SdkConfigurator.sdkSettings,
            codeLanguage,
            draftState.OrderConfigurator,
            state.ButtonConfigurator,
            state.cardFieldsConfigurator,
            state.product,
            state.integrationType,
            draftState.VaultSetupTokenConfigurator
          );
        }
      });
    case 'UPDATE_LAST_CAPTURE':
      return produce(state, (draftState) => {
        draftState.lastCapture.value = action.value;
      });
    case 'UPDATE_CARD_FIELDS_STYLES':
      return produce(state, (draftState) => {
        const { snippetReplacementKey, targetProperty, key, value } = action.value;
        set(
          draftState.cardFieldsConfigurator.value,
          [snippetReplacementKey, targetProperty, key],
          value
        );

        const codeLanguage = {
          ...state.code.language,
          status: state.code.status
        };

        draftState.code.consolidatedCode = updateConsolidatedCode(
          draftState.code.frontend,
          draftState.code.backend,
          state.SdkConfigurator.sdkSettings,
          codeLanguage,
          state.OrderConfigurator,
          state.ButtonConfigurator,
          draftState.cardFieldsConfigurator,
          state.product,
          state.integrationType,
          state.VaultSetupTokenConfigurator
        );
      });
    case 'UPDATE_LOADING_STATE':
      return produce(state, (draftState) => {
        draftState.isLoading = action.value;
      });
    case 'UPDATE_3DS_SETTING':
      return produce(state, (draftState) => {
        const codeLanguage = {
          ...state.code.language,
          status: state.code.status
        };

        draftState.OrderConfigurator.threeDsSetting = action.value;
        draftState.code.consolidatedCode = updateConsolidatedCode(
          state.code.frontend,
          state.code.backend,
          state.SdkConfigurator.sdkSettings,
          codeLanguage,
          draftState.OrderConfigurator,
          state.ButtonConfigurator,
          state.cardFieldsConfigurator,
          state.product,
          state.integrationType,
          state.VaultSetupTokenConfigurator
        );
      });
    case 'UPDATE_CONTACT_MODULE_PREFERENCE':
      return produce(state, (draftState) => {
        const codeLanguage = {
          ...state.code.language,
          status: state.code.status
        };

        draftState.OrderConfigurator.contactPreference = action.value;

        if (action.value) {
          draftState.OrderConfigurator.contactData = { shipping: hardcodedContactData };
        } else {
          draftState.OrderConfigurator.contactData = null;
        }

        draftState.code.consolidatedCode = updateConsolidatedCode(
          state.code.frontend,
          state.code.backend,
          state.SdkConfigurator.sdkSettings,
          codeLanguage,
          draftState.OrderConfigurator,
          state.ButtonConfigurator,
          state.cardFieldsConfigurator,
          state.product
        );
      });
    case 'UPDATE_FASTLANE_CONTEXT':
      return produce(state, (draftState) => {
        if (action.category) {
          draftState.fastlaneContext[action.category] = action.value;
        } else {
          // initializations
          draftState.fastlaneContext = action.value;
        }
      });
    case 'UPDATE_SHIPPING_CALLBACK_SELECTION': {
      return produce(state, (draftState) => {
        draftState.ShippingCallbackConfigurator.shippingCallbackType = action.value;
      });
    }
    default:
      return state;
  }
}
